import type { IBatchStore } from './batch-store-types';
import { DeviceStatus } from '../../constants/DeviceStatus';
import {
  BatchMode,
  BatchAction,
  MaterialType,
  WeighedMaterialType,
} from '../../constants/MaterialModels';
import { toNumber } from '../../utils/numbers';
import logger from '../../utils/logger';
import convertUnit from '../../utils/unit-converter';
import { reaction } from 'mobx';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { aeratorSwitch } from './device-switches/aerator-switch';
import { screwSwitch } from './device-switches/screw-switch';
import { beltSwitch } from './device-switches/belt-switch';
import { RemoteDisplayBatchStatus } from '../../constants/RemoteDisplayBatchStatus';
import { waterShouldWaitForMoistureProbe } from './live-moisture';
import { ToleranceMode } from '../../constants/Configuration';
import { savePumpWeightToTicket } from './save-pump-manual-weight-to-ticket';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import { IBatchData } from '../../containers/Batch/IBatchData';
import type {
  IBinEntry,
  IMaterial,
  IMaterialWithDetails,
  IMeteredMaterialModel,
} from '../../../types/material/MaterialModels';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import type {
  IBin,
  IPump,
  IScale,
} from '../../../types/device/DeviceModels';
import { isLiveMoistureInMix } from './can-batch';
import { isRunModeNewYork } from './run-mode';
import { canBatchInProportions } from './material-substitution/proportion-schedule';

const FEATURE_AUTOMOISTURE_FOR_METERED = isFeatureEnabled('automoisture_for_metered');
const FEATURE_LIVE_MOISTURE_FOR_WEIGHED = isFeatureEnabled('live_moisture_for_weighed');

export const batchMaterialMixin = {

  batchMaterial(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData) {
    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    const { constituent, material, scale, bin } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;
    const shortestDelay = Math.min(primaryBin.delay, secondaryBin ? secondaryBin.delay : primaryBin.delay);
    let primaryBinDeferredWeight = 0;
    let secondaryBinDeferredWeight = 0;

    const runningData = this.running.find(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
    const mData = runningData ? runningData.scales[scale.id].find((v: IWeighedDataToBatch) => v.material.itemId === material.itemId) : null;
    const { configuration } = this.settingsStore;

    //check if material feed finish
    if (!runningData || (mData?.isFeedFinish)) {
      logger.warn(material.code, 'feed already finish!');
      return;
    }

    // bin can have two different materials
    let binWithDifferentMaterials = 0;
    this.materialStore.assignedMaterials.forEach((m: IMaterial) => {
      if (m.bins?.length) {
        m.bins.forEach((b: IBinEntry) => {
          if (b.binId === bin.id) {
            binWithDifferentMaterials += 1;
            logger.log('bin with different materials', m.code, b.binId);
          }
        });
      }
    });

    logger.log('batch material:', material.code, 'turbo:', materialDetails.turboBatch, 'ignore tare:', configuration.ignoreTare);

    if (!materialDetails.turboBatch) {
      if (this.running.length > 1 && this.running.findIndex((v: IBatchData) => v._id === initialData._id) !== 0 && !initialData.mixerId) {
        logger.log('no turbo, batch wait', material.code);
        this.timers.set(timerKey.batchMaterial(material.itemId), () => this.batchMaterial(data, initialData), 2000, `Batch material ${material.code} - wait for turbo`);
        return;
      }
    }

    // Resume deferred feed if the device is on hold
    if (
      this.isHoldFeedDevice(primaryBin.id) &&
      this.deferredFeedDevices.has(scale.id) &&
      this.deferredFeedDevices.get(scale.id).has(primaryBin.id)
    ) {
      primaryBinDeferredWeight = this.deferredFeedDevices.get(scale.id).get(primaryBin.id);
      this.deferredFeedDevices.get(scale.id).delete(primaryBin.id);
      this.unHoldFeedDevice(primaryBin.id);
      logger.log('Resuming deferred feed in batchMaterial for primary bin', primaryBin.id, 'with weight', primaryBinDeferredWeight);
    }

    if (
      secondaryBin &&
      this.deferredFeedDevices.has(scale.id) &&
      this.deferredFeedDevices.get(scale.id).has(secondaryBin.id)
    ) {
      secondaryBinDeferredWeight = this.deferredFeedDevices.get(scale.id).get(secondaryBin.id);
      this.deferredFeedDevices.get(scale.id).delete(secondaryBin.id);
    }

    if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(primaryBin.id)) {
      this.holdBin(data.bin);
      this.timers.set(timerKey.batchMaterial(material.itemId), () => this.batchMaterial(data, initialData), 2000, `Batch material ${material.code} - hold feed`);
      return;
    }

    if (bin && !initialData.isWashout) {
      aeratorSwitch(this.rootStore, bin.aeratorId);
      screwSwitch(this.rootStore, bin.screwId);

      if (bin.beltId && !this.isBeltOn(bin.beltId)) {
        beltSwitch(this.rootStore, bin.beltId);
        this.timers.set(timerKey.batchMaterial(material.itemId), () => this.batchMaterial(data, initialData), 1000, `Batch material ${material.code} - wait for belt`);
        return;
      }

    }

    const scaleWeight = this.getScaleWeight(scale);
    const firstBinForScale = this.getFirstBinIdForTheScale(runningData, scale.id);
    const firstMaterialForScale = this.getFirstMaterialForTheScale(runningData, scale.id);

    const unit = materialDetails.itemBatchUnitSymbol || this.getUnitById(scale.unitId);
    const status = materialDetails.batchMode === BatchMode.Manual
      ? DeviceStatus.MANUAL
      : materialDetails.batchMode === BatchMode.Skip
        ? DeviceStatus.SKIP
        : DeviceStatus.IDLE;

    if (!material.visible || this.materialStore.isOtherMaterialWithTheSameOutputVisible(material)) {
      this.materialStore.makeMaterialVisible(material);
    }

    this.onBatchAction(initialData, BatchAction.NoWait);
    this.onBatchAction(initialData, BatchAction.StartBatching);

    if (firstMaterialForScale === material.code) {
      this.sendBatchCycleFeedStartEvent(scale.id);
    }

    if (runningData?.mixDesign) {
      runningData.mixDesign.forEach((v: MixDesignItem, i: number) => {
        if (v.constituentItemId === material.itemId) {
          runningData.mixDesign[i].startTare = this.getScaleWeight(scale);
          logger.log('start tare', material.code, runningData.mixDesign[i].startTare);
        }
      });
    }

    const scaleIndex = runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);
    const deduction = runningData.scales[scale.id][scaleIndex] ? runningData.scales[scale.id][scaleIndex].targetDeduction : 0;

    // ================================
    // Calculate the previous weight for the primary bin

    const hasBeenSubstituted = runningData.scales[scale.id][scaleIndex] ? runningData.scales[scale.id][scaleIndex].isSubstituted : false;
    const substitutionWeight = runningData.scales[scale.id][scaleIndex] ? runningData.scales[scale.id][scaleIndex].substitutionWeight : 0;
    // The baseline and weight recorded at the swap only apply to the feed that continues the interrupted material.
    // A substitute coming back for proportion two starts a fresh feed from the current scale weight, like any proportion two.
    const isSubstitutedFeed = hasBeenSubstituted && !data.isFeedFinishProportionOne;
    const feedSubstitutionWeight = isSubstitutedFeed ? substitutionWeight : 0;
    const scaleHasDeferredFeed = this.deferredFeedDevices.has(scale.id) && this.deferredFeedDevices.get(scale.id).size > 0;
    // If the scale has deferred feed, we know that this is
    // not the first bin that has fed for this scale
    const isFirstBinForScale = (firstBinForScale === primaryBin.id) && !scaleHasDeferredFeed;
    const hasPositiveWeightOnFirstBin = scaleWeight > 0 && isFirstBinForScale;
    const hasNegativeWeightWithTareIgnored = scaleWeight < 0 && isFirstBinForScale && configuration.ignoreTare;

    const hasSingleOrMatchingMaterial =
      binWithDifferentMaterials === 1 ||
      (binWithDifferentMaterials > 1 && firstMaterialForScale === material.code);

    const shouldZeroWeight =
      (hasPositiveWeightOnFirstBin || hasNegativeWeightWithTareIgnored || isSubstitutedFeed) &&
      hasSingleOrMatchingMaterial &&
      !data.wasPrevDataMovedWithProportion &&
      !deduction;

    const prevWeight = shouldZeroWeight ? 0 : (scaleWeight - primaryBinDeferredWeight - feedSubstitutionWeight);
    // ================================

    // ================================
    // Calculate material feed target
    let mft = toNumber(data.mft);

    if (
      this.materialStore.isWater(material.itemId)
      && !isLiveMoistureInMix(runningData)
      && runningData.allDrops > 1
      && runningData.currentDrop === runningData.allDrops
    ) {
      const overTarget = this.getOverTargetFromPrevDrops(material.itemId, runningData);
      if (overTarget > 0) {
        const adjustedMft = Math.max(0, toNumber(data.mft) - overTarget);
        data.mft = mft = adjustedMft;
      }
      logger.log(`adjusting the target ${mft} for water ${material.code}, over target in prev drops: ${overTarget}`);
    }

    if (
      canBatchInProportions(this.materialStore, material.itemId)
      // Proportion one can be empty: batch proportion two set to 100%, or a substitute taking over
      // a material that had already reached its proportion-one target.
      && (data.proportionOneTarget > 0 || data.proportionTwoTarget > 0)
    ) {
      if (runningData.scales[scale.id].length === 1) {
        mft = data.proportionOneTarget + data.proportionTwoTarget;
        data.proportionOneTarget = mft;
        data.proportionTwoTarget = 0;
      } else if (data.isFeedFinishProportionOne) {
        mft = (data.proportionOneTarget + data.proportionTwoTarget) - data.proportionOneActual;
        if (mft <= 0) {
          logger.log('no target for proportion two', material.code, mft);
          this.setFeedFinish(data, runningData, scaleIndex);
          this.batchDataForScale(scale.id, runningData);
          return;
        }
      } else {
        mft = data.proportionOneTarget;
        if (mft <= 0) {
          // Nothing to feed now: mark proportion one done so the entry moves to its proportion-two slot.
          logger.log('no target for proportion one', material.code, mft);
          this.setFeedFinish(data, runningData, scaleIndex);
          this.batchDataForScale(scale.id, runningData);
          return;
        }
      }
      data.mft = mft;
      logger.log('batch proportion:', material.code, mft);
    }

    if (configuration.onlyJogOnLastDrop && runningData.allDrops > 1 && runningData.currentDrop === runningData.allDrops) {
      data.mft = this.getRemainingTargetForLastDrop(data, runningData);
      mft = data.mft;

      if (primaryBin && constituent?.binPercent && Object.keys(constituent.binPercent).length) {
        const percent = constituent.binPercent[primaryBin.id] || 0;
        mft = mft * percent/100;
      }
    }

    if (isSubstitutedFeed) {
      mft += this.getDeviceStatus(primaryBin.id).prevWeight;
    } else if (isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative) {
      const netTarget = this.getNetTargetForScale(scale.id, runningData);
      logger.log('net target:', material.code, netTarget, mft);
      mft = netTarget + mft;
    } else if (
      (scaleWeight < 0 && !configuration.ignoreTare)
      || firstBinForScale !== bin.id
      || (binWithDifferentMaterials > 1 && (firstMaterialForScale !== material.code))
      || scaleHasDeferredFeed
      || data.wasPrevDataMovedWithProportion
      || data.isFeedFinishProportionOne
    ) {
      mft = toNumber(data.mft + scaleWeight - primaryBinDeferredWeight - feedSubstitutionWeight);
    }

    // Round target to nearest whole number for weighed materials
    mft = toNumber(mft);

    logger.log(`batchMaterial: ${material.code}! mft=${mft} data.mft=${data.mft}`, data);
    // ================================

    const materialCode = this.materialStore.getMaterialName(material);
    let primaryBinWeight = primaryBinDeferredWeight ? primaryBinDeferredWeight : 0;
    let preservedPrevWeight = prevWeight;

    if (isSubstitutedFeed) {
      const primaryBinDeviceStatus = this.getDeviceStatus(primaryBin.id);
      primaryBinWeight = 0;
      // Important: In case of substitution, we must preserve the prevWeight for balance and target to calculate correctly
      preservedPrevWeight = primaryBinDeviceStatus.prevWeight;
    }

    // Substitute entries are created as interrupted, but their proportion two is a normal fresh feed.
    if (
      data.isFeedFinishProportionOne
      && !isSubstitutedFeed
      && (!data.isFeedInterrupted || hasBeenSubstituted)
      && primaryBinDeferredWeight === 0
    ) {
      primaryBinWeight = 0;
      preservedPrevWeight = scaleWeight;
    }

    this.deviceStatus.set(primaryBin.id, {
      ...this.getDeviceStatusDefault(primaryBin),
      weight: primaryBinWeight,
      id: primaryBin.id,
      prevWeight: preservedPrevWeight,
      isSubstituted: hasBeenSubstituted,
      substitutionWeight,
      targetIsNet: hasBeenSubstituted,
      target: isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative
        ? mft
        : toNumber(data.mft),
      status: primaryBin.delay && status === DeviceStatus.IDLE ? DeviceStatus.DELAY : status,
      unit,
      currentDrop: runningData.currentDrop,
      allDrops: runningData.allDrops,
      uid: runningData.uid,
      tId: runningData.tId,
      materialCode,
    });

    if (materialDetails.batchMode === BatchMode.Skip) {
      logger.log(`${materialCode} batch mode: SKIP`);
      this.showWarning(`SKIP ${materialCode}`);

      this.deviceStatus.set(primaryBin.id, {
        ...this.getDeviceStatusDefault(primaryBin),
        id: primaryBin.id,
        materialCode,
        status: DeviceStatus.FINISH,
      });

      if (runningData.materialId) {
        this.finishBatch(runningData);
      } else {
        const index = runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);
        runningData.scales[scale.id][index].isFeedFinish = true;
        this.saveTargetForSkipMaterial(runningData, material.itemId, data.mft);
        this.batchDataForScale(scale.id, runningData);
      }

      return;
    }

    const freeFall = this.getFreeFall(primaryBin, runningData);
    const isJogModeSize = mft - toNumber(freeFall || 0) <= 0;

    if (secondaryBin) {
      if (hasBeenSubstituted) {
        this.unHoldFeedDevice(secondaryBin.id);
      }
      this.deviceStatus.set(secondaryBin.id, {
        ...this.getDeviceStatusDefault(secondaryBin),
        weight: secondaryBinDeferredWeight ? secondaryBinDeferredWeight : 0,
        id: secondaryBin.id,
        prevWeight: hasBeenSubstituted ? preservedPrevWeight : prevWeight,
        isSubstituted: hasBeenSubstituted,
        substitutionWeight,
        targetIsNet: hasBeenSubstituted,
        status: secondaryBin.delay && status === DeviceStatus.IDLE ? DeviceStatus.DELAY : status,
        unit,
        currentDrop: runningData.currentDrop,
        allDrops: runningData.allDrops,
        uid: runningData.uid,
        tId: runningData.tId,
        materialCode,
      });
    }

    this.deviceStatus.set(scale.id, {
      ...this.getDeviceStatusDefault(scale),
      id: scale.id,
      target: mft,
      status: DeviceStatus.FEED,
      weight: scaleWeight,
      unit,
      mCode: constituent ? constituent.mixItemCode : materialCode,
      currentDrop: runningData.currentDrop,
      allDrops: runningData.allDrops,
      uid: runningData.uid,
      tId: runningData.tId,
      truckCode: runningData.truckCode,
    });

    if (this.isTestMode) {
      this.checkScaleWeightInTestMode(data, runningData);
      return;
    }

    if (materialDetails.batchMode === BatchMode.Manual || scale.remoteDisplayId) {
      logger.log(`${material.code} manual batch`);

      if (scale.remoteDisplayId) {
        const { remoteDisplayStore } = this.rootStore;
        remoteDisplayStore.setStatus(scale.remoteDisplayId, {
          status: RemoteDisplayBatchStatus.START_WEIGHT,
          material: materialCode,
          target: data.mft,
        });
      }

      this.checkFeedWeight(data, runningData, mft, () => {
        this.timers.set(timerKey.batchMaterial(material.itemId), () => {
          this.checkFinalWeightForManualBatch(data, runningData, mft, (weight) => {
            this.updateInventory(data, weight, runningData);
            this.finishFeed(data, runningData, weight);
          });
        }, (primaryBin.jogCloseTime + (materialDetails.scaleSettlingTime || 2)) * 1000, `Batch material ${material.code} - manual batch check final weight`);
      });
      return;
    }

    logger.log(materialCode, 'jog mode size:', isJogModeSize, 'free fall: ', freeFall, `${mft} - ${freeFall} <= 0
              or weight > mft && firstBin`, scaleWeight, mft);

    if (
      FEATURE_LIVE_MOISTURE_FOR_WEIGHED
      && isJogModeSize
      && (
        this.materialStore.isWater(material.itemId)
        || (
          this.materialStore.isSecondWater(material.itemId)
          && toNumber(runningData.secondWater) === 100
        )
      )
      && waterShouldWaitForMoistureProbe(runningData, this)
    ) {
      logger.log('live moisture, water wait because of free fall', freeFall, mft, materialCode);

      this.deviceStatus.set(primaryBin.id, {
        ...this.getDeviceStatus(primaryBin.id),
        status: DeviceStatus.WAIT_MOIST,
      });

      if (secondaryBin) {
        this.deviceStatus.set(secondaryBin.id, {
          ...this.getDeviceStatus(secondaryBin.id),
          status: DeviceStatus.WAIT_MOIST,
        });
      }

      this.timers.set(timerKey.batchMaterial(material.itemId), () => {
        this.batchMaterial(data, initialData);
      }, 1000, `Batch material ${material.code} - wait for moisture`);

      return;
    }

    this.timers.set(timerKey.device(primaryBin.id), () => {
      logger.log(`feed: ${materialCode}, bin: ${primaryBin.description}, delay: ${primaryBin.delay} sec`);

      if (materialDetails.type === WeighedMaterialType.FineAggregate) {
        this.onBatchAction(initialData, BatchAction.FineAggStart);
      }

      if (isJogModeSize || (scaleWeight > mft && firstBinForScale === data.bin.id)) {
        if (
          materialDetails.liveMoisture
          && (FEATURE_AUTOMOISTURE_FOR_METERED || FEATURE_LIVE_MOISTURE_FOR_WEIGHED)
        ) {
          this.adjustWaterTarget(data, initialData, mft);
        } else {
          this.jogMode(data, initialData, mft);
        }
      } else {
        this.feedMode(data, runningData, mft);
      }

      this.checkScaleWeightForMaterial(data);

    }, (shortestDelay || 0) * 1000, `Feed bin #${primaryBin.id} ${materialCode} with delay ${shortestDelay}s`); // Bin with the shortest delay will open first
  },

  saveTargetForSkipMaterial(this: IBatchStore, data: IBatchData, materialId: number, target: number) {
    if (data?.mixDesign) {
      const item = data.mixDesign?.find((v: MixDesignItem) => v.constituentItemId === materialId);
      if (item) {
        item.targetPerDrop = Number(target);
        this.saveResultForDrop(data, item);
      }
    }
  },

  /**
   * Skip materials without a bin or pump are not on any scale or pump, so record their drop target here, the way
   * batchMaterial and batchMetered do for a skip material on a device.
   */
  saveTargetsForSkipMaterialsWithoutDevice(this: IBatchStore, data: IBatchData) {
    if (!data?.mixDesign || data.materialId) {
      return;
    }

    data.mixDesign.forEach((constituent: MixDesignItem) => {
      if (!(Number(constituent.quantityFinal) > 0) || !this.materialStore.isSkipMaterialWithoutDevice(constituent)) {
        return;
      }

      const material = constituent.batchCode
        ? this.materialStore.getMaterialByBatchCode(constituent.batchCode)
        : this.materialStore.getMaterialByItemId(constituent.constituentItemId);
      const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
      let target;
      // Classify by the plant material: when matched by batch code, the constituent's id can differ from it.
      if (this.materialStore.isWater(material.itemId) || this.materialStore.isSecondWater(material.itemId)) {
        // Follow the sequence's head/wash water split per drop, as batchMetered and batchMaterial do for water.
        const waterTargets = this.getHeadWashWaterTarget(data, this.getTargetForWater(constituent, data), material.code);
        target = waterTargets.headWater + waterTargets.washWater;
      } else if (material.assignment === MaterialType.Metered) {
        target = this.getTargetForMetered(material, materialDetails, constituent, data);
      } else {
        target = this.getTargetForWeighed(constituent, data);
      }

      logger.log(`${material.code} batch mode: SKIP, no device`, target);
      this.showWarning(`SKIP ${material.code}`);
      // Record the constituent itself: when matched by batch code, its id can differ from the plant material's.
      constituent.targetPerDrop = Number(target);
      this.saveResultForDrop(data, constituent);
    });
  },

  checkFinalWeightForManualBatch(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    runningData: IBatchData,
    mft: number,
    onFinish: (weight: number) => void = () => {/* empty */},
  ) {
    const { material, scale } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;

    this.timers.set(timerKey.checkFinalWeight(material.itemId), () => {
      const tolerance = this.getTolerance(materialDetails, mft);
      const weight = this.getCurrentWeightForBin(primaryBin.id);
      const b1s = this.getDeviceStatus(primaryBin.id);

      if (weight > mft + tolerance?.over && !b1s.errorsOverridden.includes(DeviceStatus.OVER)) {
        this.deviceStatus.set(primaryBin.id, {
          ...b1s,
          weight: this.getBinWeightForStatus(b1s, weight),
          status: DeviceStatus.OVER,
          error: true,
        });

        const materialCode = this.materialStore.getMaterialName(material);
        const unit = scale.unitId ? this.getUnitById(scale.unitId) : materialDetails.itemBatchUnitSymbol;
        this.showError({ key: 'Current weight is over target! Press Override button [F2]', props: { material: materialCode }});
        logger.warn(`${materialCode}: Current weight is over target! ${weight} > ${mft} + ${tolerance?.over}, `, weight, mft, tolerance, this.isOverride);
        this.showOverTolerancePopup({
          material: materialCode,
          deviceId: primaryBin.id,
          weight,
          target: mft,
          unit,
          uid: runningData.uid,
          currentDrop: runningData.currentDrop,
        });

        this.checkFinalWeightForManualBatch(data, runningData, mft, onFinish);

      } else {
        this.deviceStatus.set(primaryBin.id, {
          ...b1s,
          weight: this.getBinWeightForStatus(b1s, weight),
          status: DeviceStatus.SETTLING,
          error: false,
        });

        onFinish(weight);
      }

    }, 1000, `Check final weight for manual batch - ${material.code}`);
  },

  monitorScaleInventory(this: IBatchStore) {
    const { settingsStore } = this.rootStore;

    let timers = {};

    if (!this.devicesStore.scales.length) {
      this.setTimeout(() => {
        this.monitorScaleInventory();
      }, 1000);

      return;
    }

    if (this.canDemoBatch) {
      logger.log('monitor scale inventory');
    } else if (!this.canEmit || !this.pacConnected || this.isDemo || !this.devicesStore.scales.length) {
      this.setTimeout(() => {
        this.monitorScaleInventory();
      }, 1000);

      return;
    }

    let scaleData: any = {};
    const resetItem = (scaleId: number) => {
      scaleData[scaleId] = {
        activeBin: null,
        itemId: null,
        itemCode: null,
      };
    };

    const setScaleWeight = (scaleId: number, weight: number = 0) => {
      this.scaleWeights[scaleId] = weight;
      resetItem(scaleId);
    };

    this.devicesStore.scales.forEach((scale: IScale) => {
      setScaleWeight(scale.id, this.getScaleWeight(scale));
    });

    reaction(() => this.channels,
      () => {
        const { configuration } = settingsStore;
        if (this.maintenanceMode && !configuration?.reduceMaintenanceModeInventory) {
          return;
        }

        this.devicesStore.scales.forEach((scale: IScale) => {
          const weight = this.getScaleWeight(scale);

          if (this.checkIndicatorValueError(weight) === DeviceStatus.INDICATOR_ERROR) {
            logger.log('indicator error', scale.id, scale.description);
            return;
          }

          if (!this.replayMode && this.canReduceInventory && !this.running.length && !scale.decumulative && weight + scale.startTare > this.scaleWeights[scale.id] && weight > scale.startTare) {
            logger.debug('inventory monitoring:', scale.description, weight, this.scaleWeights[scale.id]);

            // @ts-ignore
            this.clearTimeout(timers[scale.id]);

            const bin = this.devicesStore.bins.find((v: IBin) => v.scaleId === scale.id && this.isOutputOn(v.gateOnePNo));
            if (bin) {
              scaleData[scale.id].activeBin = bin;

              const material = this.materialStore.getMaterialForBin(bin.id);
              const md = material ? this.materialStore.materialDetails[material.materialDetailId] : null;
              if (md) {
                scaleData[scale.id].itemId = md.itemId;
                scaleData[scale.id].itemCode = md.itemCode;
              }
            }

            // @ts-ignore
            timers[scale.id] = this.setTimeout(() => {
              if (this.checkIndicatorValueError(weight) === DeviceStatus.INDICATOR_ERROR) {
                logger.log('indicator error', scale.id, scale.description);
                return;
              }

              const weightToUpdate = weight - this.scaleWeights[scale.id];
              setScaleWeight(scale.id, weight);

              if (scaleData[scale.id].itemId) {
                const material = this.materialStore.materialsWithDetails.find((v: IMaterialWithDetails) => v.itemId === scaleData[scale.id].itemId);
                const quantity = toNumber(convertUnit(weightToUpdate, material.itemBatchUnitSymbol, material.itemInventoryUnitSymbol), 4);
                logger.log('inventory material', material.code, quantity, material.itemInventoryUnitSymbol, '=', weightToUpdate, this.getUnitById(scale.unitId), scaleData[scale.id]);

                if (quantity > 0 && weightToUpdate > scale.startTare && scaleData[scale.id].activeBin) {
                  this.materialStore.updateMaterialInventoryUse(material, {
                    quantity,
                    comment: 'Manual',
                    isAutomatic: false,
                    binIds: [ scaleData[scale.id].activeBin.id ],
                  });
                }
              }

              if (!this.running.length && weightToUpdate > scale.startTare && scaleData[scale.id].itemId) {
                const payload = {
                  quantity: weightToUpdate,
                  deviceId: scale.id,
                  deviceDescription: scale.description,
                  unitId: scale.unitId,
                  unitSymbol: this.getUnitById(scale.unitId),
                  itemId: scaleData[scale.id].itemId,
                  itemCode: scaleData[scale.id].itemCode,
                };
                this.ticketStore.createSimpleTicket(payload);
                logger.log('simple ticket:', payload);
                resetItem(scale.id);
              }

            }, 3000);
          } else if (!scale.decumulative && this.scaleWeights[scale.id] > weight && weight <= scale.dischargeTare) {
            setScaleWeight(scale.id, weight);
          }
        });
      },
    );
  },

  updateMaterialInventoryForPumps(this: IBatchStore, pump: IPump, material: IMaterial, weight: number, isAutomatic: boolean = false) {
    const md = this.materialStore.materialDetails[material.materialDetailId] || {};
    let qty = weight * (md.meterRate || 1);
    let unit = md.itemBatchUnitSymbol;

    if (pump.unitId && pump.unitId !== md.itemBatchUnitId) {
      unit = this.getUnitById(pump.unitId);
    }

    const quantity = toNumber(convertUnit(qty, unit, material.itemInventoryUnitSymbol), 4);
    const ds = this.getDeviceStatus(pump.id);
    const typeOfUse = isAutomatic
      ? ''
      : ds.manualFeedStarted
        ? 'Manual CB'
        : 'Manual';
    logger.log('inventory material', material.code, qty, unit, '=', quantity, material.itemInventoryUnitSymbol,
      'meter rate:', md.meterRate || 1, typeOfUse);

    if (this.canReduceInventory && quantity > 0) {
      this.materialStore.updateMaterialInventoryUse(material, {
        quantity,
        comment: typeOfUse,
        ticket: ds.tId,
        isAutomatic,
      });
    }
  },

  setPumpWeight(this: IBatchStore, pumpId: number, weight: number = 0) {
    this.pumpWeights[pumpId] = weight;
  },

  monitorPumpInventory(this: IBatchStore) {
    const { settingsStore } = this.rootStore;

    if (!this.devicesStore.pumps.length) {
      this.setTimeout(() => {
        this.monitorPumpInventory();
      }, 1000);
      return;
    }

    if (this.canDemoBatch) {
      logger.log('monitor pump inventory');
    } else if (!this.canEmit || !this.pacConnected || this.isDemo) {
      this.setTimeout(() => {
        this.monitorPumpInventory();
      }, 1000);
      return;
    }

    this.devicesStore.pumps.forEach((pump: IPump) => {
      this.setPumpWeight(pump.id, this.getPumpWeight(pump));
    });

    let meters = [ ...this.meters ];
    const getLastPumpWeight = (pump: IPump) => {
      return toNumber(meters[pump.meterPNo - 1], 2);
    };

    reaction(() => this.meters,
      () => {
        const { configuration } = settingsStore;
        if (this.maintenanceMode && !configuration?.reduceMaintenanceModeInventory) {
          return;
        }

        if (this.canEmit && !this.replayMode && (this.canDemoBatch || (this.pacConnected && !this.isDemo))) {
          this.devicesStore.pumps.forEach((pump: IPump) => {
            if (pump.meterPNo) {
              const material = this.materialStore.materials.find((v: IMeteredMaterialModel) => v.pumpId === pump.id) || {};
              const md = this.materialStore.materialDetails[material.materialDetailId] || {};
              let weight = this.getPumpWeight(pump);
              const lastWeight = getLastPumpWeight(pump);
              const pumpStatus = this.getDeviceStatus(pump.id);
              const isAutoFeed = pumpStatus.status === DeviceStatus.FEED;
              const canCreateSimpleTicket = !this.running.length || (this.running.length && this.running[0].material && this.running[0].material.itemId === material.itemId);

              if (lastWeight === weight) {
                return;
              }

              if (weight > this.pumpWeights[pump.id] && material.code) {

                logger.debug('inventory monitoring:', material.code, weight, this.pumpWeights[pump.id], 'isAutoFeed', isAutoFeed, pumpStatus.status);

                if ((pumpStatus.status === DeviceStatus.FINISH || pumpStatus.status === DeviceStatus.IDLE) && pumpStatus.manualFeedStarted) {
                  const d = this.running.length ? this.running[0] : null;

                  if (d?.mixerId && d?.temperWaterPNo === pump.meterPNo) {
                    const tempeWaterWeight = (weight * (md.meterRate || 1)) - d.temperWaterStart;
                    this.deviceStatus.set(pump.id, {
                      ...pumpStatus,
                      weight: tempeWaterWeight,
                    });
                    logger.log('temper water:', pump.description, material.code, weight, tempeWaterWeight);
                  } else {
                    this.deviceStatus.set(pump.id, {
                      ...pumpStatus,
                      weight: (weight - this.pumpWeights[pump.id]) * (md.meterRate || 1),
                    });
                  }
                }

                // @ts-ignore
                this.clearTimeout(this[`pump-inventory-monitoring-timer-${pump.id}`]);

                // @ts-ignore
                this[`pump-inventory-monitoring-timer-${pump.id}`] = this.setTimeout(() => {
                  weight = this.getPumpWeight(pump);
                  const weightToUpdate = weight - this.pumpWeights[pump.id];
                  this.setPumpWeight(pump.id, weight);
                  const quantity = weightToUpdate * (md.meterRate || 1);

                  logger.log('inventory monitoring update:', material.code, weightToUpdate, quantity, 'isAutoFeed', isAutoFeed);
                  this.updateMaterialInventoryForPumps(pump, material, weightToUpdate, isAutoFeed);

                  if (canCreateSimpleTicket) {
                    const payload = {
                      quantity,
                      deviceId: pump.id,
                      deviceDescription: pump.description,
                      itemId: material.itemId,
                      itemCode: material.code,
                      unitId: md.itemBatchUnitId,
                      unitSymbol: md.itemBatchUnitSymbol,
                    };
                    logger.log('simple ticket:', payload);
                    this.ticketStore.createSimpleTicket(payload);
                  } else if (!isAutoFeed) {
                    savePumpWeightToTicket(pump, this);
                  }

                }, 3000);

              } else if (this.pumpWeights[pump.id] > weight && material.code) {
                this.setPumpWeight(pump.id, weight);
              }
            }
          });
        }

        meters = [ ...this.meters ];
      });
  },

  getOverTargetFromPrevDrops(itemId: number, data: IBatchData): number {
    const batchDrops = data?.batchDrops || {};

    if (!Object.keys(batchDrops).length) {
      return 0;
    }

    let overTarget = 0;
    let itemCode = '';

    Object.keys(batchDrops).forEach((dropNumber) => {
      const drop = batchDrops[dropNumber];

      if (!Array.isArray(drop)) {
        return;
      }

      drop.forEach((item: any) => {
        if (item?.constituentItemId === itemId) {
          itemCode = item.constituentItemCode;
          const actual = Number(item.actualPerDrop);
          const target = Number(item.targetPerDrop);
          if (Number.isFinite(actual) && Number.isFinite(target) && actual > target) {
            overTarget += (actual - target);
          }
        }
      });
    });

    logger.log(`over target from prev drops: ${itemCode || itemId}`, overTarget);
    return overTarget;
  },
};
